import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Form, { Actions, Conditional, EmailAddressValidator, Password, RequiredValidator, Select, TextField } from '../../../Components/Form';
import { useToasts } from '../../../Components/Toasts';
import API from '../../../Services/API';
import Icon from '../../../Components/Icons/Icon';
import Loading from '../../../Components/Loading';
import Recaptcha from '../../../Services/Recaptcha';
import { useOnboarding } from '../OnboardingProvider';
import StepShell from '../Components/StepShell';

const loadCountries = async () => await import('../../../Data/countries.js');

// Final step: create the account. Adapted from
// Pages/Auth/Challenges/RegisterChallenge.jsx, with the wizard's collected
// data sent along as the register call's `onboarding` payload.
const AccountStep = () => {
    const {voucherCode, buildRegisterPayload, clearDraft, back} = useOnboarding();
    const [isGB, setIsGB] = useState(true);
    const [countries, setCountries] = useState(null);
    const [registering, setRegistering] = useState(false);
    const [voucherLocked, setVoucherLocked] = useState(true);
    const {showSuccess, showError} = useToasts();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        Recaptcha.init();
        loadCountries().then(module => {
            setCountries(module.default);
        });
    }, []);

    const register = async (data) => {
        try {
            const captchaToken = await Recaptcha.getToken('register');

            const payload = {
                ...data,
                captchaToken,
                onboarding: {
                    ...buildRegisterPayload(),
                    firstName: data.firstName,
                    lastName: data.lastName,
                },
            };

            return API.post('/users/register', payload)
                .then((res) => {
                    waitForRegistrationComplete(res.userId, data.email);
                })
                .catch(err => {
                    showError(API.parseErrorResponse(err));
                });
        } catch (err) {
            showError(API.parseErrorResponse(err));
        }
    };

    const waitForRegistrationComplete = (userId, email) => {
        setRegistering(true);

        API.get('/users/waitForRegistration?userId=' + userId).then(() => {
            clearDraft();
            navigate('/login', {state: {email}});
        }).catch(err => {
            showError(API.parseErrorResponse(err));
            setRegistering(false);
        });
    };

    const submit = async (data) => {
        if (isGB) {
            return register(data);
        }

        return await API.post('/interests', data)
            .then(() => {
                showSuccess('Thank you!', 'We\'ll let you know when we\'re available in your area.');
            })
            .catch(err => {
                showError(API.parseErrorResponse(err));
            });
    };

    if (registering) {
        return <div className="my-4">
            <Loading size="2x" center />
            <p className="text-center text-muted text-sm mt-4">
                <strong>Welcome!</strong><br />
                Setting up your services and website…
            </p>
        </div>;
    }

    return <StepShell
        title="Create your account"
        intro={<>
            <p>Nearly there — tell us who you are and choose your login details.</p>
        </>}
    >
        <Form
            data={{
                voucherCode: voucherCode ?? 'FREE-PRO',
                email: searchParams.get('email'),
                country: 'GB',
            }}
            onSubmit={submit}
        >
            {countries && <Select label="Country"
                name="country"
                options={Object.keys(countries).map(name => ({label: name, value: countries[name]}))}
                onChange={v => setIsGB(v === 'GB')}
                validators={[RequiredValidator]}
            />}
            {!countries && <Select label="Country"
                name="country"
                options={[{label: 'United Kingdom', value: 'GB'}]}
                disabled
            />}

            <Conditional condition={(data) => data.country !== 'GB'}>
                <div className="prose prose-sm surface-2 p-2 rounded-lg">
                    <p>Sorry, we're only operating in the UK at the moment.</p>

                    <p>We plan to expand into other areas soon.</p>

                    <p>Would you like to be notified when we're available in your area?</p>
                </div>
                <TextField
                    name="name"
                    label="Name"
                    validators={isGB ? [] : [RequiredValidator]}
                />
            </Conditional>

            <Conditional condition={(data) => data.country === 'GB'}>
                <TextField
                    name="firstName"
                    label="First name"
                    validators={isGB ? [RequiredValidator] : []}
                />
                <TextField
                    name="lastName"
                    label="Surname"
                    validators={isGB ? [RequiredValidator] : []}
                />
            </Conditional>

            <TextField type="email"
                name="email"
                label="Email"
                validators={[RequiredValidator, EmailAddressValidator]}
            />

            <Conditional condition={(data) => data.country === 'GB'}>
                <Password label="Password"
                    name="password"
                    validators={isGB ? [RequiredValidator] : []}
                    showPrompts
                />
                <TextField label="Voucher code"
                    name="voucherCode"
                    validators={[]}
                    disabled={voucherLocked}
                    suffixOnClick={() => setVoucherLocked(false)}
                    suffix={voucherLocked ? <Icon icon="Pencil" /> : null}
                />
            </Conditional>

            <Actions submit={{label: isGB ? 'Create account' : 'Keep me updated'}} cancel={{label: 'Back', onClick: back}} />
        </Form>
    </StepShell>;
};

export default AccountStep;
